import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { workRpc } from '@/features/workMode/api';
import type {
  ComponentLot,
  ComponentLotOnHand,
  QcResult,
  SerializedUnit,
  SerializedUnitDetail,
  SerializedUnitRow,
  SerializedUnitStatus,
  ShipUnitsResult,
} from './types';

/**
 * Serialized units — data layer for the Unit log and the bench.
 *
 * Reads go at the tables (RLS: org-wide read). Every write goes through a
 * `serialized_*` RPC so the actor is stamped and the timeline gets an event
 * (migration 142).
 */

export const unitKeys = {
  all: ['serialized'] as const,
  list: (days: number | null) => ['serialized', 'units', days] as const,
  detail: (id: string | null) => ['serialized', 'detail', id] as const,
  bySerial: (serial: string) => ['serialized', 'serial', serial.toUpperCase()] as const,
  lotsOnHand: (ids: string[]) => ['serialized', 'lots-on-hand', [...ids].sort().join(',')] as const,
  salesOrders: () => ['serialized', 'sales-orders'] as const,
  customers: () => ['serialized', 'customers'] as const,
};

const UNIT_SELECT =
  '*, items(id, sku, name), builder:built_by(id, full_name, email), inspector:qc_by(id, full_name, email), ' +
  'workstations(id, name), customers(id, name), sales_orders(id, so_number, ship_to), shipments(id, shipment_number, tracking_number)';

/** Every serialized unit, newest first. `days` null = all time. */
export function useSerializedUnits(days: number | null = null) {
  return useQuery({
    queryKey: unitKeys.list(days),
    queryFn: async () => {
      let q = supabase
        .from('serialized_units' as never)
        .select(UNIT_SELECT)
        .order('built_at', { ascending: false })
        .limit(5000);
      if (days) q = q.gte('built_at', new Date(Date.now() - days * 86_400_000).toISOString());
      const { data, error } = await q;
      if (error) throw error;
      return (data as unknown as SerializedUnitRow[]) ?? [];
    },
    staleTime: 15_000,
  });
}

export function useSerializedUnitRow(id: string | null) {
  return useQuery({
    queryKey: ['serialized', 'row', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('serialized_units' as never)
        .select(UNIT_SELECT)
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as SerializedUnitRow | null) ?? null;
    },
  });
}

/** Unit + timeline. */
export function useSerializedUnitDetail(id: string | null) {
  return useQuery({
    queryKey: unitKeys.detail(id),
    enabled: !!id,
    queryFn: () => workRpc<SerializedUnitDetail | null>('serialized_unit_detail', { p_unit_id: id }),
  });
}

/** Look a unit up by its barcode value (case-insensitive). */
export async function findUnitBySerial(serial: string): Promise<SerializedUnitRow | null> {
  const s = serial.trim();
  if (!s) return null;
  // ilike without wildcards is a case-insensitive equality; the client-side
  // check guards against a serial that happens to contain a pattern char.
  const { data, error } = await supabase
    .from('serialized_units' as never)
    .select(UNIT_SELECT)
    .ilike('serial_number', s)
    .limit(5);
  if (error) throw error;
  const rows = (data as unknown as SerializedUnitRow[]) ?? [];
  return rows.find((r) => r.serial_number.toUpperCase() === s.toUpperCase()) ?? rows[0] ?? null;
}

/** Lot numbers on hand (or recently received) for the given component items. */
export function useComponentLotsOnHand(itemIds: string[]) {
  return useQuery({
    queryKey: unitKeys.lotsOnHand(itemIds),
    enabled: itemIds.length > 0,
    staleTime: 2 * 60_000,
    queryFn: async () => {
      const rows = await workRpc<ComponentLotOnHand[]>('work_component_lots_on_hand', { p_item_ids: itemIds });
      const byItem = new Map<string, ComponentLotOnHand[]>();
      for (const r of rows ?? []) {
        const arr = byItem.get(r.item_id) ?? [];
        arr.push(r);
        byItem.set(r.item_id, arr);
      }
      return byItem;
    },
  });
}

// ---------------------------------------------------------------------------
// Lookups for the ship dialog
// ---------------------------------------------------------------------------

export interface SalesOrderOption {
  id: string;
  so_number: string;
  status: string;
  ordered_at: string;
  customer_id: string | null;
  customer_email: string | null;
  ship_to: { name?: string | null; city?: string | null; country?: string | null; country_code?: string | null } | null;
  customers: { id: string; name: string } | null;
}

export function useSalesOrderOptions(enabled = true) {
  return useQuery({
    queryKey: unitKeys.salesOrders(),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_orders' as never)
        .select('id, so_number, status, ordered_at, customer_id, customer_email, ship_to, customers(id, name)')
        .order('ordered_at', { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data as unknown as SalesOrderOption[]) ?? [];
    },
  });
}

export interface CustomerOption {
  id: string;
  name: string;
  shipping_address: { city?: string | null; country?: string | null } | null;
}

export function useCustomerOptions(enabled = true) {
  return useQuery({
    queryKey: unitKeys.customers(),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers' as never)
        .select('id, name, shipping_address')
        .eq('is_active', true)
        .order('name')
        .limit(500);
      if (error) throw error;
      return (data as unknown as CustomerOption[]) ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function safeSegment(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'x';
}

/**
 * Upload a unit photo to `work-captures/<org>/units/<serial>/…`. Keyed by
 * serial so every photo of a unit sits together even before the row exists.
 */
export async function uploadUnitPhoto(orgId: string, serial: string, file: File | Blob, name = 'photo.jpg'): Promise<string> {
  const path = `${orgId}/units/${safeSegment(serial.toUpperCase())}/${Date.now()}-${safeSegment(name)}`;
  const { error } = await supabase.storage.from('work-captures').upload(path, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: (file as File).type || 'image/jpeg',
  });
  if (error) throw error;
  return path;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function invalidateUnits(qc: ReturnType<typeof useQueryClient>, id?: string | null) {
  void qc.invalidateQueries({ queryKey: ['serialized', 'units'] });
  void qc.invalidateQueries({ queryKey: ['serialized', 'serial'] });
  if (id) {
    void qc.invalidateQueries({ queryKey: unitKeys.detail(id) });
    void qc.invalidateQueries({ queryKey: ['serialized', 'row', id] });
  }
}

export function useRegisterUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      serial: string;
      itemId: string;
      builtAt?: string | null;
      builtBy?: string | null;
      workstationId?: string | null;
      photoPaths?: string[];
      labelPhotoPath?: string | null;
      componentLots?: ComponentLot[];
      qcResult?: QcResult | null;
      qcNotes?: string | null;
      notes?: string | null;
    }) =>
      workRpc<SerializedUnit>('serialized_unit_register', {
        p_serial: input.serial,
        p_item_id: input.itemId,
        p_built_at: input.builtAt ?? null,
        p_built_by: input.builtBy ?? null,
        p_workstation_id: input.workstationId ?? null,
        p_photo_paths: input.photoPaths ?? [],
        p_label_photo_path: input.labelPhotoPath ?? null,
        p_component_lots: input.componentLots ?? [],
        p_qc_result: input.qcResult ?? null,
        p_qc_notes: input.qcNotes ?? null,
        p_notes: input.notes ?? null,
      }),
    onSuccess: (u) => invalidateUnits(qc, u?.id),
  });
}

export function useQcUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; result: QcResult; notes?: string | null }) =>
      workRpc<SerializedUnit>('serialized_unit_qc', { p_unit_id: input.id, p_result: input.result, p_notes: input.notes ?? null }),
    onSuccess: (_u, vars) => invalidateUnits(qc, vars.id),
  });
}

export function useSetUnitStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: Exclude<SerializedUnitStatus, 'shipped'>; note?: string | null }) =>
      workRpc<SerializedUnit>('serialized_unit_set_status', { p_unit_id: input.id, p_status: input.status, p_note: input.note ?? null }),
    onSuccess: (_u, vars) => invalidateUnits(qc, vars.id),
  });
}

export function useAddUnitPhotos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; paths: string[]; labelPhotoPath?: string | null; note?: string | null }) =>
      workRpc<SerializedUnit>('serialized_unit_add_photos', {
        p_unit_id: input.id,
        p_paths: input.paths,
        p_label_photo_path: input.labelPhotoPath ?? null,
        p_note: input.note ?? null,
      }),
    onSuccess: (_u, vars) => invalidateUnits(qc, vars.id),
  });
}

export function useSetUnitLots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; lots: ComponentLot[]; note?: string | null }) =>
      workRpc<SerializedUnit>('serialized_unit_set_lots', { p_unit_id: input.id, p_lots: input.lots, p_note: input.note ?? null }),
    onSuccess: (_u, vars) => invalidateUnits(qc, vars.id),
  });
}

export function useAddUnitNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; note: string }) =>
      workRpc<SerializedUnitDetail>('serialized_unit_note', { p_unit_id: input.id, p_note: input.note }),
    onSuccess: (detail, vars) => {
      if (detail) qc.setQueryData(unitKeys.detail(vars.id), detail);
      invalidateUnits(qc, vars.id);
    },
  });
}

export function useShipUnits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      serials: string[];
      salesOrderId?: string | null;
      shipmentId?: string | null;
      customerId?: string | null;
      destination?: string | null;
      shippedAt?: string | null;
      note?: string | null;
    }) =>
      workRpc<ShipUnitsResult>('serialized_units_ship', {
        p_serials: input.serials,
        p_sales_order_id: input.salesOrderId ?? null,
        p_shipment_id: input.shipmentId ?? null,
        p_customer_id: input.customerId ?? null,
        p_destination: input.destination ?? null,
        p_shipped_at: input.shippedAt ?? null,
        p_note: input.note ?? null,
      }),
    onSuccess: () => invalidateUnits(qc),
  });
}

export function useDeleteUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase.from('serialized_units' as never).delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_r, vars) => invalidateUnits(qc, vars.id),
  });
}
